import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { InputFormField } from "../../../formfields/InputFormField";
import { FormButtons } from "../../FormButtons";
import { FormHeader } from "../../FormHeader";
import {
  LabNotebookExperiment,
  LabNotebookExperimentStatusList,
  labNotebookExperimentFieldLabels,
  labNotebookExperimentsConstants,
} from "../../../../ELN/types/LabNotebookExperiment";
import { SelectFormField } from "../../../formfields/SelectFormField";
import { TextareaFormField } from "../../../formfields/TextareaFormField";
import { useCallback } from "react";
import { QuickEditChildrenProps } from "../common/QuickEditWrapper";
import { useQuickEditSubmit } from "../common/useQuickEditHooks";
import { LabNotebooksVirtualizedSelectForm } from "../../EntityForms/formsVirtualized/LabNotebooksVirtualizedSelectFormField";
import { LabNotebookExperimentFormValues } from "../../../../ELN/LabNotebookExperiments/Forms/LabNotebookExperimentForm";

export const QuickEditLabNotebookExperimentForm = ({
  onEdit,
  onCancel,
  initialValues,
}: QuickEditChildrenProps<LabNotebookExperiment>) => {
  const { onSubmit } = useQuickEditSubmit<LabNotebookExperiment>({
    resource: labNotebookExperimentsConstants.resource,
    onEdit: onEdit,
    onCancel: onCancel,
  });

  const FormSchema = yup.object().shape({
    labNotebook: yup.object().required("A lab notebook is required"),
    name: yup
      .string()
      .required("Lab notebook experiment name is required")
      .typeError("Lab notebook experiment name is required"),
    status: yup.object().required("Lab notebook status is required").typeError("Lab notebook status is required"),
  });

  const entityToFormValues = useCallback((entity?: LabNotebookExperiment) => {
    return !!entity
      ? ({
          ...entity,
          status: {
            id: entity.status,
            name: entity.status.charAt(0) + entity.status.slice(1).toLowerCase(),
            value: entity.status,
          },
        } as LabNotebookExperimentFormValues)
      : {};
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<LabNotebookExperimentFormValues>({
    defaultValues: entityToFormValues(initialValues),
    resolver: yupResolver(FormSchema),
  });

  const formValuesToEntity = useCallback(
    (entity: LabNotebookExperimentFormValues) =>
      entity.id && onSubmit(entity.id, { ...entity, status: entity.status.value } as LabNotebookExperiment),
    [onSubmit]
  );

  return (
    <>
      <FormHeader title={`Edit ${labNotebookExperimentsConstants.entitySingular}`} subtitle={initialValues?.name} />

      <LabNotebooksVirtualizedSelectForm
        id="labNotebook"
        label={labNotebookExperimentFieldLabels.labNotebook}
        control={control}
        showControls
        horizontal
      />

      <InputFormField
        id="name"
        label={labNotebookExperimentFieldLabels.name}
        errors={errors}
        register={register}
        autoFocus={true}
        required
        placeholder={`Enter ${labNotebookExperimentsConstants.entitySingular} name...`}
      />

      <SelectFormField
        id="status"
        label={labNotebookExperimentFieldLabels.status}
        items={LabNotebookExperimentStatusList.map((s) => {
          return { id: s, name: s.charAt(0) + s.slice(1).toLowerCase(), value: s };
        })}
        control={control}
        required
        menuPortalTarget={document.body}
      />

      <TextareaFormField
        id="description"
        label={labNotebookExperimentFieldLabels.description}
        errors={errors}
        register={register}
        placeholder="Enter description..."
      />

      <FormButtons
        onClose={onCancel}
        onSubmit={handleSubmit(formValuesToEntity)}
        disabled={isSubmitting}
        errors={errors}
      />
    </>
  );
};
